import { SSR_QUERY } from '@/router/query-params'
import { idleCallbackPromise } from '@/utils/helpers'

import type { DevtoolsPanel, DevtoolsRecord, DevtoolsSource } from './types'
import { resolveUiState } from './ui-state'

const MAX_RECORDS = 2000

export class Devtools {
    readonly records: DevtoolsRecord[] = []

    readonly panels = new Map<string, DevtoolsPanel>()

    private readonly listeners = new Set<() => void>()

    private readonly recordReaders = new Set<(record: DevtoolsRecord) => void>()

    private lastRecordId = 0

    private disposed = false

    private readonly adRequestIds = new WeakMap<object, number>()

    record(source: DevtoolsSource, name: string, data: Record<string, unknown> = {}) {
        if (this.disposed) {
            return
        }
        const record: DevtoolsRecord = {
            id: ++this.lastRecordId,
            time: performance.now(),
            source,
            name,
            data: { ...data },
            adRequestId: this.adRequestIds.get(data),
        }
        this.records.push(record)
        this.recordReaders.forEach((reader) => reader(record))
        if (this.records.length > MAX_RECORDS) {
            this.records.shift()
        }
        this.notify()
    }

    markAdRequest(event: object, adRequestId: number) {
        this.adRequestIds.set(event, adRequestId)
    }

    dispose() {
        this.disposed = true
        this.records.length = 0
        this.panels.clear()
        this.listeners.clear()
        this.recordReaders.clear()
    }

    readRecords(reader: (record: DevtoolsRecord) => void): () => void {
        this.records.forEach(reader)
        this.recordReaders.add(reader)
        return () => this.recordReaders.delete(reader)
    }

    addPanel(panel: Promise<DevtoolsPanel>): () => void {
        let removed = false
        let loaded: DevtoolsPanel | undefined
        panel.then((value) => {
            if (!removed) {
                loaded = value
                this.panels.set(value.id, value)
                this.notify()
            }
        })
        return () => {
            removed = true
            if (loaded && this.panels.get(loaded.id) === loaded) {
                this.panels.delete(loaded.id)
                this.notify()
            }
        }
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    private notify() {
        this.listeners.forEach((listener) => listener())
    }
}

export function getDevtools(): Devtools | undefined {
    return import.meta.env.SSR ? undefined : window.pgDevtools
}

export function initDevtools() {
    if (window.pgDevtools) {
        return
    }
    window.pgOpenDevtools = () => reloadWithQuery(SSR_QUERY.DEVTOOLS, '1')
    const uiState = resolveUiState()
    if (!uiState) {
        return
    }

    const devtools = new Devtools()
    window.pgDevtools = devtools

    const pageLoaded =
        document.readyState === 'complete'
            ? Promise.resolve()
            : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }))

    pageLoaded
        .then(() => idleCallbackPromise())
        .then(() => import('./ui/mount'))
        .then(({ mountDevtools }) => mountDevtools(devtools, uiState))
}

export function reloadWithQuery(param: string, value: string) {
    const params = new URLSearchParams(window.location.search)
    params.set(param, value)
    window.location.search = params.toString()
}
